import React, { useCallback } from "react";
import { Node, Edge } from "reactflow";
import { message } from "antd";
import { Action, ActionType } from "../utils/types";

// Hook for action history management
export const useActionHistory = (
  nodes: Node[],
  edges: Edge[],
  actionHistory: Action[],
  setActionHistory: React.Dispatch<React.SetStateAction<Action[]>>,
  setNodes: (nodes: Node[]) => void,
  setEdges: (edges: Edge[]) => void
) => {
  // Safe cloning function for React Flow nodes that handles functions and other non-serializable data
  const safeCloneNodes = (nodes: Node[]): Node[] => {
    // Optimized version that produces identical results to JSON.parse(JSON.stringify(nodes))
    // but performs much better by avoiding the string serialization step
    const cloneSerializableValue = (value: any): any => {
      if (value === null || value === undefined) return value;
      if (typeof value === "function") return undefined; // JSON.stringify excludes functions
      if (typeof value === "symbol") return undefined; // JSON.stringify excludes symbols
      if (typeof value === "boolean" || typeof value === "number" || typeof value === "string") {
        return value;
      }
      if (Array.isArray(value)) {
        return value.map(cloneSerializableValue);
      }
      if (typeof value === "object") {
        const cloned: any = {};
        for (const key in value) {
          if (value.hasOwnProperty(key)) {
            const clonedValue = cloneSerializableValue(value[key]);
            if (clonedValue !== undefined) {
              cloned[key] = clonedValue;
            }
          }
        }
        return cloned;
      }
      return undefined;
    };

    
    return nodes.map(cloneSerializableValue).filter(node => node !== undefined);
  };

  const saveToHistory = useCallback((actionType: ActionType) => {
    const nodesCopy = safeCloneNodes(nodes);
    // Optimize edge cloning too, but edges are simpler than nodes
    const edgesCopy = edges.map(edge => {
      const cloned: any = {};
      for (const key in edge) {
        if (edge.hasOwnProperty(key)) {
          const value = (edge as any)[key];
          if (typeof value !== "function" && typeof value !== "symbol") {
            if (value && typeof value === "object" && !Array.isArray(value)) {
              cloned[key] = { ...value }; // Shallow clone objects
            } else {
              cloned[key] = value;
            }
          }
        }
      }
      return cloned;
    });
    
    setActionHistory((prev: Action[]) => {
      const newAction: Action = {
        type: actionType,
        nodes: nodesCopy,
        edges: edgesCopy
      };
      
      // Limit action history to prevent memory issues (keep last 30 actions - reduced from 50)
      const maxHistorySize = 30;
      const newHistory = [...prev, newAction];
      
      // If we exceed the limit, remove the oldest actions
      if (newHistory.length > maxHistorySize) {
        return newHistory.slice(newHistory.length - maxHistorySize);
      }
      
      return newHistory;
    });
  }, [nodes, edges, setActionHistory]);

  const undoLastAction = useCallback(() => {
    if (actionHistory.length === 0) {
      message.info("Nothing to undo");
      return;
    }
    
    const lastAction = actionHistory[actionHistory.length - 1];
    setNodes(lastAction.nodes);
    setEdges(lastAction.edges);
    setActionHistory(actionHistory.slice(0, actionHistory.length - 1));
    
    message.success(`Undid last ${lastAction.type} action`);
  }, [actionHistory, setActionHistory, setNodes, setEdges]);

  return { saveToHistory, undoLastAction };
};